import { useMemo } from 'react';

import {
  type Deserializer,
  ifDefined,
  parseJSON,
  type Serializer,
  StorageEngineFactory,
  TypedStorageAdapter,
} from '@enterprise/core';

import type { UseTypedStorageOptions } from './types/use-typed-storage.options';

import { useStorage } from './storage.hook';

/**
 * A custom React hook that provides an interface for using session storage with typed values.
 * It utilizes a `TypedStorageAdapter` to handle serialization and deserialization of values,
 * allowing for convenient storage and retrieval of complex data types.
 *
 * @template TValue - The type of the value to be stored and retrieved from session storage.
 * @param {string} key - The key under which the value will be stored in session storage.
 * @param {TValue} [initialValue] - An optional initial value to be set in session storage
 * if the key does not already exist.
 * @param {UseTypedStorageOptions<TValue>} [options] - Optional configuration for serialization, deserialization,
 * and storage engine options.
 * @returns A tuple containing the current value from session storage and an API object with methods to manipulate
 * the storage.
 */
export const useSessionStorage = <TValue = unknown>(
  key: string,
  initialValue?: TValue,
  options?: UseTypedStorageOptions<TValue>
) => {
  const deserialize: Deserializer<TValue> = useMemo(() => {
    return (
      options?.deserialize ??
      ((value: string | undefined) => {
        return ifDefined(value, (raw) => {
          const result = parseJSON<TValue>(raw);

          if (result.isFailure()) {
            return undefined;
          }

          return result.unwrap();
        });
      })
    );
  }, [options]);

  const serialize: Serializer<TValue> = useMemo(() => {
    return (
      options?.serialize ??
      ((value: TValue | undefined) => {
        return JSON.stringify(value);
      })
    );
  }, [options]);

  const { storageOptions } = options ?? {
    deserialize,
    serialize,
  };

  const adapter = useMemo(() => {
    return new TypedStorageAdapter(
      StorageEngineFactory.getInstance(sessionStorage, { ...storageOptions, channelName: 'session-storage' }),
      serialize,
      deserialize
    );
  }, [deserialize, serialize, storageOptions]);

  return useStorage({
    adapter,
    initialValue,
    key,
  });
};
